// The Info card's three multi-step actions as pure sequences over their
// dependencies, so the ORDER is a unit-tested contract rather than a hook's
// private business (review, PR #278). The hook in InfoPanel.tsx wires them.

export interface AlertActionDeps {
  /** The endpoint this browser's own push subscription holds, or null. */
  localEndpoint: () => Promise<string | null>;
  /** The VAPID key that subscription was made under, or null when the browser does not expose it. */
  localKey: () => Promise<string | null>;
  api: (path: string, opts: { method: 'POST' | 'DELETE'; body?: unknown; quiet: true }) => Promise<unknown>;
  /** Subscribe this browser with the server's VAPID key (the OS prompt is the phone's own). */
  subscribe: (publicKey: string) => Promise<PushSubscriptionJSON>;
  /** Release this browser's push subscription. */
  unsubscribe: () => Promise<void>;
  /** Re-read the server's view of this phone. */
  readServer: () => Promise<void>;
  userAgent: () => string;
}

/**
 * Turn on. A subscription this browser holds that the server does not — the
 * push service disowned it (the server dropped the row on a 404/410), or it was
 * orphaned — is released FIRST: `subscribe()` with the same key hands an
 * existing subscription back unchanged, so without this the same dead endpoint
 * would be posted and refused every time (review, PR #278, round 3). So is one
 * made under an OLD VAPID key, whatever the server holds: subscribe() throws
 * on the key mismatch and every send under the new key is refused (round 6).
 * The one the server already holds, under the current key, is kept;
 * subscribe() returns it and the row is refreshed in place.
 */
export async function turnOnSequence(deps: AlertActionDeps, server: { publicKey: string | null; serverEndpoint: string | null }): Promise<void> {
  if (!server.publicKey) throw new Error('Shift alerts are not set up on the server yet.');
  const local = await deps.localEndpoint();
  const key = local ? await deps.localKey() : null;
  if (local && (local !== server.serverEndpoint || (key !== null && key !== server.publicKey))) await deps.unsubscribe();
  const json = await deps.subscribe(server.publicKey);
  await deps.api('/me/push', { method: 'POST', body: { ...json, userAgent: deps.userAgent() }, quiet: true });
  await deps.readServer();
}

/**
 * Turn off: the SERVER row goes first, named by this phone's endpoint, so a
 * stale card on an old phone can only ever switch off itself — and a DELETE
 * that fails leaves the browser subscription in place, the card On, and Turn
 * off there to retry. Only then is the browser subscription released.
 */
export async function turnOffSequence(deps: AlertActionDeps): Promise<void> {
  const endpoint = await deps.localEndpoint();
  await deps.api('/me/push', { method: 'DELETE', body: endpoint ? { endpoint } : undefined, quiet: true });
  await deps.unsubscribe();
  await deps.readServer();
}

/**
 * Send a test, naming this phone's endpoint so a card still reading On on a
 * phone that has since been replaced can only test ITSELF (the server answers
 * 404 for any other phone on file). However it ends, re-read the server — a
 * push service that has disowned the phone makes the server drop the row and
 * answer 404, and the card must read that, not its cached On.
 */
export async function testSequence(deps: AlertActionDeps): Promise<void> {
  const endpoint = await deps.localEndpoint();
  try {
    await deps.api('/me/push/test', { method: 'POST', body: endpoint ? { endpoint } : undefined, quiet: true });
  } finally {
    await deps.readServer();
  }
}
